import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Layout from "@/components/layout";
import SeoHead from "@/components/seo-head";
import PathwaySelectorRequest from "@/components/forms/pathway-selector-request";
import { loadStandardContent } from "@/lib/content/site.server";
import { listCourses } from "@/lib/content/client.server";
import { trackJourneyEvent } from "@/utils/analytics";
import {
  evaluatePathway,
  getPathwayRouteDisplay,
  normalizePathwayAnswers,
  pathwayCourseKeyFromHint,
  validatePathwayHints,
  buildBusinessTrainingUrl,
  getPathwayGuidanceDisplay,
  type PathwayAnswers,
  type CurrentPathwayCourseRecord,
} from "@/utils/pathway-rules";

type Choice = { value: string; label: string; hint?: string };
type Step = {
  field: keyof PathwayAnswers;
  question: string;
  description?: string;
  choices: Choice[];
  multi?: boolean;
};

const bookingChoices: Choice[] = [
  { value: "self", label: "Myself" },
  { value: "team", label: "My staff or team" },
  { value: "self_and_team", label: "Myself and my team" },
  { value: "someone_else", label: "Someone else" },
  { value: "unknown", label: "I’m not sure yet" },
];

const backgroundChoices: Choice[] = [
  { value: "beginner", label: "I’m new to beauty and aesthetics" },
  { value: "beauty_therapist", label: "I work in beauty therapy" },
  { value: "aesthetics_practitioner", label: "I already practise aesthetics" },
  { value: "medical_professional", label: "I’m a medical professional" },
  { value: "other", label: "Another background" },
  { value: "unknown", label: "I’m not sure how to describe it" },
];

const qualificationChoices: Choice[] = [
  { value: "none", label: "I do not hold a relevant qualification" },
  { value: "vtct_level_3_access_aesthetic_therapies", label: "VTCT Level 3 Access to Aesthetic Therapies" },
  { value: "vtct_level_4_advanced_aesthetic_therapies_skin", label: "VTCT Level 4 Advanced Aesthetic Therapies for Skin" },
  { value: "vtct_level_5_non_surgical_skin_procedures", label: "VTCT Level 5 Non-Surgical Aesthetic Procedures for Skin" },
  { value: "vtct_level_4_laser_hair_removal", label: "VTCT Level 4 Laser Hair Removal" },
  { value: "vtct_level_5_laser_tattoo_removal", label: "VTCT Level 5 Laser Tattoo Removal" },
  { value: "vtct_level_6_mesotherapy", label: "VTCT Level 6 Mesotherapy" },
  { value: "medical_professional_qualification", label: "A medical professional qualification" },
  { value: "cpd_certificate", label: "A CPD certificate relevant to this area" },
  { value: "other", label: "Another qualification" },
  { value: "unknown", label: "I’m unsure of the exact qualification" },
];

const yesNoUnsure: Choice[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "I’m not sure" },
];

const treatmentChoices: Choice[] = [
  { value: "beauty_facials", label: "Beauty treatments and facials" },
  { value: "advanced_skin", label: "Advanced skin treatments" },
  { value: "laser_ipl", label: "Laser or IPL" },
  { value: "tattoo_removal", label: "Laser tattoo removal" },
  { value: "injectables", label: "Injectables" },
  { value: "pmu", label: "Permanent make-up" },
  { value: "hair", label: "Hair" },
  { value: "teaching_assessing", label: "Teaching or assessing" },
  { value: "unsure", label: "I’m still exploring" },
];

const intentChoices: Choice[] = [
  { value: "starting_out", label: "Learn a new area from the beginning" },
  { value: "specific_skill", label: "Add one specific skill" },
  { value: "regulated_qualification", label: "Work towards a regulated qualification" },
  { value: "prior_learning_review", label: "Ask whether previous learning can be reviewed" },
  { value: "unsure", label: "I’m not sure yet" },
];

const professionChoices: Choice[] = [
  { value: "doctor", label: "Doctor" },
  { value: "nurse", label: "Nurse" },
  { value: "dentist", label: "Dentist" },
  { value: "pharmacist", label: "Pharmacist" },
  { value: "other_regulated", label: "Another regulated healthcare profession" },
  { value: "not_medical", label: "I do not work in a regulated medical profession" },
  { value: "unknown", label: "I’m not sure which category applies" },
];

const stepsFor = (answers: PathwayAnswers): Step[] => {
  const steps: Step[] = [
    {
      field: "bookingFor",
      question: "Who is the training for?",
      description: "This decides whether we show individual guidance or the staff-training planner.",
      choices: bookingChoices,
    },
    {
      field: "primaryBackground",
      question: "Which best describes your starting position?",
      description: "Your background helps us frame the options. It does not confirm eligibility.",
      choices: backgroundChoices,
    },
  ];

  steps.push({
    field: "careerChange",
    question: "Are you changing careers?",
    description: "Changing careers does not tell us which qualifications or experience you already have.",
    choices: yesNoUnsure,
  });

  steps.push(
    {
      field: "declaredQualifications",
      question: "Which relevant qualification do you currently hold?",
      description: "Choose each relevant award you hold. If it is not listed, choose another qualification and explain it only if you request a review.",
      choices: qualificationChoices,
      multi: true,
    },
    {
      field: "priorCpd",
      question: "Have you completed CPD training in this area?",
      choices: yesNoUnsure,
    },
    {
      field: "treatmentGoal",
      question: "What would you like to train in?",
      choices: treatmentChoices,
    },
    {
      field: "trainingIntent",
      question: "What would you like the training to help you do?",
      choices: intentChoices,
    },
  );

  if (
    answers.primaryBackground === "medical_professional" ||
    answers.treatmentGoal === "injectables"
  ) {
    steps.push(
      {
        field: "medicalProfession",
        question: "Which medical profession best describes you?",
        description: "We do not ask for registration numbers in this quiz.",
        choices: professionChoices,
      },
      {
        field: "registrationEvidenceAvailable",
        question: "Could you provide relevant professional registration evidence if admissions asks for it?",
        choices: yesNoUnsure,
      },
    );
  }

  steps.push(
    {
      field: "deliveryPreference",
      question: "How would you prefer to learn?",
      description: "A preference does not change practical or assessment requirements.",
      choices: [
        { value: "in_person", label: "In person" },
        { value: "blended", label: "Blended learning" },
        { value: "online", label: "Online" },
        { value: "flexible", label: "I can be flexible" },
        { value: "unknown", label: "I’m not sure" },
      ],
    },
    {
      field: "canAttendPractical",
      question: "Could you attend practical training if your route requires it?",
      choices: yesNoUnsure,
    },
    {
      field: "startTimeframe",
      question: "When might you want to start?",
      choices: [
        { value: "soon", label: "As soon as a suitable option is available" },
        { value: "within_three_months", label: "Within three months" },
        { value: "three_to_six_months", label: "In three to six months" },
        { value: "exploring", label: "I’m exploring for now" },
        { value: "unknown", label: "I’m not sure" },
      ],
    },
  );
  return steps;
};

export default function BestAestheticCourseForYou({
  siteData,
  courseRecords,
}: {
  siteData: any;
  courseRecords: CurrentPathwayCourseRecord[];
}) {
  const router = useRouter();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const requestDetailsRef = useRef<HTMLDetailsElement>(null);
  const [answers, setAnswers] = useState<PathwayAnswers | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [someoneElseMode, setSomeoneElseMode] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [draftQualifications, setDraftQualifications] = useState<string[]>([]);
  const [answeredFields, setAnsweredFields] = useState<Set<string>>(() => new Set());
  const started = useRef(false);

  useEffect(() => {
    if (!router.isReady || answers) return;
    const hints = validatePathwayHints(router.query as Record<string, unknown>, courseRecords);
    setAnswers(
      normalizePathwayAnswers({
        entryPersona: hints.persona ?? "unknown",
        courseHint: hints.courseHint,
        bookingFor: "unknown",
      }, courseRecords),
    );
  }, [router.isReady, router.query, answers, courseRecords]);

  const steps = useMemo(() => (answers ? stepsFor(answers) : []), [answers]);
  const currentStep = steps[stepIndex];
  const evaluation = useMemo(
    () => (answers ? evaluatePathway(answers, new Date(), courseRecords) : null),
    [answers, courseRecords],
  );
  const guidance = getPathwayGuidanceDisplay(evaluation?.guidanceFamily);
  const routeDisplays = evaluation
    ? evaluation.optionIds.flatMap((id) => {
        const display = getPathwayRouteDisplay(id, courseRecords);
        return display ? [display] : [];
      })
    : [];

  useEffect(() => {
    if (answers && !showResult) headingRef.current?.focus();
  }, [stepIndex, showResult, answers]);

  useEffect(() => {
    if (!answers || !currentStep) return;
    if (!answeredFields.has(currentStep.field)) {
      setDraftValue("");
      if (currentStep.field === "declaredQualifications") setDraftQualifications([]);
      return;
    }
    const storedValue = answers[currentStep.field];
    if (currentStep.field === "declaredQualifications" && Array.isArray(storedValue)) {
      setDraftQualifications(storedValue);
    }
    setDraftValue(Array.isArray(storedValue) ? storedValue[0] ?? "" : String(storedValue ?? ""));
  }, [answers, answeredFields, currentStep?.field]);

  const trackStart = (currentAnswers: PathwayAnswers) => {
    if (started.current) return;
    started.current = true;
    trackJourneyEvent("pathway_started", {
      persona: currentAnswers.entryPersona,
      placement: "quiz",
      entry_page_key: "quiz",
    });
  };

  useEffect(() => {
    if (!showResult || !answers || !evaluation) return;
    trackJourneyEvent("pathway_result_viewed", {
      persona: answers.entryPersona,
      outcome: evaluation.outcome,
      guidance_family: evaluation.guidanceFamily ?? "general",
      rules_version: evaluation.rulesVersion,
      placement: "quiz",
    });
  }, [showResult, answers, evaluation]);

  const choose = (value: string) => {
    if (!answers || !currentStep) return;
    trackStart(answers);
    const raw = { ...answers, [currentStep.field]: currentStep.field === "declaredQualifications" ? [value] : value };
    const next = normalizePathwayAnswers(raw, courseRecords);
    setAnswers(next);
    setAnsweredFields((current) => new Set(current).add(currentStep.field));
    if (currentStep.field === "bookingFor" && value !== "someone_else") {
      setSomeoneElseMode(false);
    }
    trackJourneyEvent("pathway_step_completed", {
      step_key: String(currentStep.field),
      placement: "quiz",
    });
    if (currentStep.field === "bookingFor" && (value === "team" || value === "self_and_team")) {
      trackJourneyEvent("pathway_team_redirected", {
        persona: next.entryPersona,
        booking_intent: value,
        placement: "quiz",
      });
      window.location.assign(buildBusinessTrainingUrl({
        persona: next.entryPersona,
        courseHint: next.courseHint,
        placement: "quiz",
      }));
      return;
    }
    if (currentStep.field === "bookingFor" && value === "someone_else") {
      setSomeoneElseMode(true);
      setShowResult(true);
      return;
    }
    if (stepIndex + 1 >= stepsFor(next).length) {
      setShowResult(true);
    } else {
      setStepIndex((index) => index + 1);
    }
  };

  const chooseQualifications = () => {
    if (!answers || !currentStep || !draftQualifications.length) return;
    trackStart(answers);
    const next = normalizePathwayAnswers({ ...answers, declaredQualifications: draftQualifications }, courseRecords);
    setAnswers(next);
    setAnsweredFields((current) => new Set(current).add(currentStep.field));
    trackJourneyEvent("pathway_step_completed", {
      step_key: String(currentStep.field),
      placement: "quiz",
    });
    setStepIndex((index) => index + 1);
  };

  const restart = () => {
    const hints = validatePathwayHints(router.query as Record<string, unknown>);
    setAnswers(normalizePathwayAnswers({
      entryPersona: hints.persona ?? "unknown",
      courseHint: hints.courseHint,
      bookingFor: "unknown",
    }, courseRecords));
    setStepIndex(0);
    setShowResult(false);
    setSomeoneElseMode(false);
    setAnsweredFields(new Set());
    started.current = false;
  };

  const editAnswers = () => {
    setShowResult(false);
    setStepIndex(0);
  };

  return (
    <>
      <SeoHead meta={{
        title: "Find Your Aesthetic Training Route | Cosmetic College",
        description: "Answer a few questions to explore training options. Admissions confirms course entry requirements before you book.",
        canonicalPath: "/best-aesthetic-course-for-you/",
      }} />
      <Layout siteData={siteData}>
        <main className="min-h-[70vh] bg-[linear-gradient(135deg,#f7f4ee_0%,#fff_48%,#edf7f5_100%)] px-4 py-10 sm:py-16">
          <div className="mx-auto max-w-4xl">
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <Link href="/which-aesthetics-course-is-right-for-me/" className="text-sm font-semibold text-brand-700 underline underline-offset-4">
                ← Compare training routes
              </Link>
              <button type="button" onClick={restart} className="min-h-11 rounded-full border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-800 hover:border-brand-500">
                Start again
              </button>
            </div>

            <section className="overflow-hidden rounded-[2rem] border border-stone-200 bg-white shadow-[0_24px_80px_rgba(35,46,42,0.12)]">
              <div className="border-b border-stone-200 bg-stone-950 px-6 py-8 text-white sm:px-10">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal-300">A thoughtful next step</p>
                <h1 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">Find a training route that fits your starting point</h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-stone-300 sm:text-base">
                  Prefer to compare every level side by side? See{" "}
                  <Link
                    href="/which-aesthetics-course-is-right-for-me/"
                    className="font-semibold text-teal-200 underline underline-offset-4 hover:text-white"
                  >
                    which aesthetics course is right for me
                  </Link>
                  .
                </p>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-stone-300 sm:text-base">Your answers stay in this page while you explore. You can see useful guidance before choosing whether to contact admissions.</p>
              </div>

              <div className="p-6 sm:p-10">
                {!answers ? (
                  <p role="status" className="text-stone-600">Loading the route questions…</p>
                ) : showResult && evaluation ? (
                  <div aria-live="polite">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-700">Your starting point</p>
                    <h2 className="mt-2 text-2xl font-semibold text-stone-950 sm:text-3xl">
                      {someoneElseMode
                        ? "Share this guide with the learner"
                        : evaluation.outcome === "suggested_options" && routeDisplays.length
                          ? "Explore these suggested options"
                          : "A review is the right next step"}
                    </h2>
                    <p className="mt-4 max-w-3xl leading-7 text-stone-700">
                      {someoneElseMode
                        ? "We do not collect another adult’s qualifications or professional details. Share this guide so they can explore routes using their own answers."
                        : evaluation.outcome === "suggested_options" && routeDisplays.length
                          ? "These options match an approved route rule and the current course records. They remain provisional until entry requirements and evidence are checked."
                          : "Course entry depends on the exact award, units and evidence you hold. This guide cannot confirm eligibility, professional registration or insurance suitability. Admissions can review your circumstances before you book."}
                    </p>
                    {!someoneElseMode && evaluation.outcome === "suggested_options" && routeDisplays.length ? (
                      <div className="mt-7 grid gap-4 md:grid-cols-2">
                        {routeDisplays.slice(0, 3).map((route) => (
                          <article key={route.optionId} className="rounded-2xl border border-teal-200 bg-teal-50/60 p-5">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-900">Suggested route</p>
                            <h3 className="mt-2 font-semibold text-stone-950">{route.title}</h3>
                            <p className="mt-2 text-sm leading-6 text-stone-700">{route.summary}</p>
                            {route.steps.length ? <ol className="mt-4 space-y-2 text-sm text-stone-700">{route.steps.map((step) => <li key={step.id}><span className="font-semibold">{step.title}</span>{step.explanation ? ` — ${step.explanation}` : ""}{step.course ? <span> <Link href={step.course.url} onClick={() => trackJourneyEvent("pathway_course_clicked", { course_key: pathwayCourseKeyFromHint(step.course?.url, courseRecords) ?? "unknown", route_key: route.optionId, placement: "quiz" })} className="font-semibold text-brand-800 underline underline-offset-4">View {step.course.title}</Link></span> : null}</li>)}</ol> : null}
                            <p className="mt-4 text-sm leading-6 text-stone-700">What still needs checking: {route.reviewRequirements.join("; ") || "confirm current course entry requirements with admissions."}</p>
                          </article>
                        ))}
                      </div>
                    ) : null}
                    {!someoneElseMode ? (
                      <p className="mt-5 text-sm leading-6 text-stone-700">
                        Planning Level 3 to Level 7? See our{" "}
                        <Link href="/pathway-to-aesthetics/" className="font-semibold text-brand-800 underline underline-offset-4">
                          Pathway to Aesthetics course
                        </Link>
                        .
                      </p>
                    ) : null}

                    {someoneElseMode ? (
                      <div className="mt-7 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
                        Ask the learner to answer the questions themselves before sending any enquiry. Do not enter their contact details, qualifications or medical registration information here.
                      </div>
                    ) : null}
                    {answers.deliveryPreference === "online" && answers.canAttendPractical === "no" ? (
                      <p className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                        You prefer online learning and cannot currently attend practical training. Some goals require supervised practical work or assessment, so ask admissions which options fit before booking.
                      </p>
                    ) : null}

                    {evaluation.outcome !== "suggested_options" || !routeDisplays.length ? <article className="mt-7 rounded-2xl border border-teal-200 bg-teal-50/70 p-5 sm:p-6">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-900">Explore this course family</p>
                      <h3 className="mt-2 text-xl font-semibold text-stone-950">{guidance.title}</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-700">{guidance.text}</p>
                      <Link href={guidance.href} className="mt-4 inline-flex min-h-11 items-center rounded-full bg-stone-950 px-5 text-sm font-semibold text-white hover:bg-brand-700">
                        Browse current options
                      </Link>
                    </article> : null}

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {!someoneElseMode ? <div className="rounded-xl border border-stone-200 p-4">
                        <h3 className="font-semibold text-stone-900">What to have ready</h3>
                        <p className="mt-2 text-sm leading-6 text-stone-600">If you request a review, share the exact qualification names and relevant training history you know. Do not include client or patient information.</p>
                      </div> : null}
                      <div className="rounded-xl border border-stone-200 p-4">
                        <h3 className="font-semibold text-stone-900">Your result status</h3>
                        <p className="mt-2 text-sm leading-6 text-stone-600">{evaluation.status === "information_needed" ? "More information is needed to narrow the route." : "A human entry review is needed before a course-specific suggestion can be made."}</p>
                      </div>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                      <button type="button" onClick={editAnswers} className="min-h-11 rounded-full border border-stone-300 px-5 text-sm font-semibold text-stone-800 hover:border-brand-500">Change my answers</button>
                      {!someoneElseMode ? (evaluation.nextAction === "rpl_review" ? <Link href="/courses/rpl/#rpl-enquiry" className="inline-flex min-h-11 items-center rounded-full bg-brand-700 px-5 text-sm font-semibold text-white hover:bg-brand-800">Request a prior-learning review</Link> : <button type="button" onClick={() => {
                        if (!requestDetailsRef.current) return;
                        requestDetailsRef.current.open = true;
                        requestAnimationFrame(() => requestDetailsRef.current?.querySelector<HTMLElement>("summary")?.focus());
                      }} className="min-h-11 rounded-full bg-brand-700 px-5 text-sm font-semibold text-white hover:bg-brand-800">Ask admissions to review my options</button>) : null}
                      <Link href="/training-course-calendar/" className="inline-flex min-h-11 items-center rounded-full px-5 text-sm font-semibold text-brand-800 underline underline-offset-4">Browse course dates</Link>
                    </div>

                    {!someoneElseMode ? <div className="mt-10 border-t border-stone-200 pt-8">
                      <details ref={requestDetailsRef}>
                        <summary className="min-h-11 cursor-pointer py-2 font-semibold text-stone-900">Would you like to send your route to admissions?</summary>
                        <p className="mb-5 max-w-2xl text-sm leading-6 text-stone-600">This is optional. Your answers are prefilled for you to review. A suggestion is not an admission or insurance decision.</p>
                        <PathwaySelectorRequest
                          sourcePage="/best-aesthetic-course-for-you/"
                          placement="quiz"
                          initialAnswers={answers as unknown as Record<string, unknown>}
                          pathwayContext={{ answers: answers as unknown as Record<string, unknown>, result: evaluation }}
                        />
                      </details>
                    </div> : null}
                    <p className="mt-8 border-t border-stone-100 pt-5 text-xs text-stone-500">Guidance rules version: {evaluation.rulesVersion}. This is a provisional route review, not confirmation of eligibility.</p>
                  </div>
                ) : currentStep ? (
                  <div>
                    <div className="mb-8">
                      <div className="flex items-center justify-between gap-4 text-sm text-stone-600">
                        <span>Question {stepIndex + 1} of {steps.length}</span>
                        <span>{Math.round(((stepIndex + 1) / steps.length) * 100)}% complete</span>
                      </div>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-stone-100" role="progressbar" aria-label="Quiz progress" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={stepIndex + 1}>
                        <div className="h-full rounded-full bg-teal-600 transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} />
                      </div>
                    </div>
                    <h2 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold tracking-tight text-stone-950 sm:text-3xl">{currentStep.question}</h2>
                    {currentStep.description ? <p className="mt-3 max-w-2xl text-sm leading-6 text-stone-600">{currentStep.description}</p> : null}

                    <fieldset className="mt-7 grid gap-3 sm:grid-cols-2">
                      <legend className="sr-only">{currentStep.question}</legend>
                      {currentStep.choices.map((choice) => (
                        <label key={choice.value} className={`flex min-h-16 cursor-pointer items-center gap-4 rounded-xl border p-4 text-sm font-medium text-stone-800 transition hover:border-teal-600 hover:bg-teal-50 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-teal-700 ${(currentStep.multi ? draftQualifications.includes(choice.value) : draftValue === choice.value) ? "border-teal-700 bg-teal-50 ring-1 ring-teal-600" : "border-stone-200 bg-white"}`}>
                          <input
                            type={currentStep.multi ? "checkbox" : "radio"}
                            name={`pathway-${currentStep.field}`}
                            value={choice.value}
                            checked={currentStep.multi ? draftQualifications.includes(choice.value) : draftValue === choice.value}
                            onChange={() => {
                              if (currentStep.multi) {
                                setDraftQualifications((selected) => {
                                  const exclusive = choice.value === "none" || choice.value === "unknown";
                                  if (selected.includes(choice.value)) return selected.filter((value) => value !== choice.value);
                                  return exclusive ? [choice.value] : [...selected.filter((value) => value !== "none" && value !== "unknown"), choice.value];
                                });
                              } else {
                                setDraftValue(choice.value);
                              }
                            }}
                            className="h-5 w-5 shrink-0 border-stone-400 text-teal-700 focus:ring-teal-600"
                          />
                          <span>{choice.label}</span>
                        </label>
                      ))}
                    </fieldset>

                    <div className="mt-8 flex justify-between border-t border-stone-100 pt-5">
                      <button type="button" disabled={stepIndex === 0} onClick={() => setStepIndex((index) => Math.max(0, index - 1))} className="min-h-11 rounded-full px-4 text-sm font-semibold text-stone-700 underline underline-offset-4 disabled:invisible">Back</button>
                      <button type="button" disabled={currentStep.multi ? !draftQualifications.length : !draftValue} onClick={currentStep.multi ? chooseQualifications : () => choose(draftValue)} className="min-h-11 rounded-full bg-brand-700 px-6 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-stone-300">Continue</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
            <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-5 text-stone-500">You can browse courses directly at any time. A route suggestion never replaces admissions checks, assessment requirements or the existing course booking process.</p>
          </div>
        </main>
      </Layout>
    </>
  );
}

export async function getStaticProps() {
  const [siteData, courseResponse] = await Promise.all([
    loadStandardContent(),
    listCourses({ limit: 200, select: "sys.id,fields.title,fields.url,fields.hidden" }).catch(() => ({ items: [] })),
  ]);
  const courseRecords: CurrentPathwayCourseRecord[] = courseResponse.items
    .filter((entry: any) => entry?.sys?.id && entry?.fields?.title && entry?.fields?.url)
    .map((entry: any) => ({
      id: String(entry.sys.id),
      title: String(entry.fields.title),
      url: String(entry.fields.url),
      active: entry.fields.hidden !== true,
    }));
  return { props: { siteData, courseRecords }, revalidate: 3600 };
}
